/**
 * Digest preferences API service
 *
 * Provides methods for coaches to manage their simplified digest notification preferences.
 * Implements the simplified "one toggle, one time" model where digests are sent daily at
 * 10:00 AM in the coach's timezone.
 */

import { getApiUrl } from '../config/api';
import { safeJsonParse } from '../utils/apiUtils';

export interface DigestPreferences {
  digest_enabled: boolean;
  digest_timezone: string;
}

export interface DigestPreferencesResponse extends DigestPreferences {
  message?: string;
}

/**
 * Get the current user's digest preferences
 */
export const getDigestPreferences = async (): Promise<DigestPreferences> => {
  const token = localStorage.getItem('access');

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(getApiUrl('/api/users/me/digest-preferences/'), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const result = await safeJsonParse<DigestPreferences>(response, 'fetching digest preferences');
  
  if (!result.success) {
    if (response.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    throw new Error(result.error || `Failed to fetch digest preferences: ${response.statusText}`);
  }

  return result.data!;
};

/**
 * Update the current user's digest preferences
 */
export const updateDigestPreferences = async (
  preferences: Partial<DigestPreferences>
): Promise<DigestPreferencesResponse> => {
  const token = localStorage.getItem('access');

  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(getApiUrl('/api/users/me/digest-preferences/'), {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(preferences),
  });

  const result = await safeJsonParse<DigestPreferencesResponse>(response, 'updating digest preferences');
  
  if (!result.success) {
    if (response.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    if (response.status === 403) {
      throw new Error('Only coaches can configure digest preferences.');
    }
    throw new Error(result.error || `Failed to update digest preferences: ${response.statusText}`);
  }

  return result.data!;
};
